// AdminAnalyticsTab.jsx — ULTRA EDITION (API Mode)

import { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI } from '../utils/api';

/* ══════════════════════════════════════════════════════════════
   REQUIRED — utils/api.js mein ye add karo:

   getAnalyticsOverview:      (period) => api.get(`/admin/analytics/overview?period=${period}`),
   getAnalyticsGrowth:        (period) => api.get(`/admin/analytics/user-growth?period=${period}`),
   getAnalyticsTrend:         (period) => api.get(`/admin/analytics/bookings-trend?period=${period}`),
   getAnalyticsCities:        ()       => api.get('/admin/analytics/cities'),
   getAnalyticsTopPerformers: ()       => api.get('/admin/analytics/top-performers'),
   getAnalyticsVendorTypes:   ()       => api.get('/admin/analytics/vendor-types'),
   getAnalyticsHeatmap:       (period) => api.get(`/admin/analytics/heatmap?period=${period}`),
   getAnalyticsActivity:      (limit)  => api.get(`/admin/analytics/activity?limit=${limit}`),
   getAnalyticsRetention:     ()       => api.get('/admin/analytics/retention'),
══════════════════════════════════════════════════════════════ */

/* ─── CONSTANTS ─── */
const PERIODS = [
  { key: 'today',   label: 'Aaj' },
  { key: 'week',    label: '7 Din' },
  { key: 'month',   label: '30 Din' },
  { key: '3months', label: '3 Mahine' },
  { key: 'year',    label: '1 Saal' },
];

const VENDOR_ICONS = {
  decorator:'🎨', photographer:'📸', mehendi_artist:'🌸',
  dj:'🎵', makeup_artist:'💄', cake_catering:'🎂',
  tent_furniture:'⛺', videographer:'🎥', anchor:'🎤', wedding_car:'🚗',
};

const STATUS_META = {
  pending:     { color: '#f59e0b', label: 'Pending',     icon: '⏳' },
  confirmed:   { color: '#3b82f6', label: 'Confirmed',   icon: '✅' },
  in_progress: { color: '#8b5cf6', label: 'In Progress', icon: '🔄' },
  completed:   { color: '#10b981', label: 'Completed',   icon: '🎉' },
  cancelled:   { color: '#ef4444', label: 'Cancelled',   icon: '❌' },
  disputed:    { color: '#dc2626', label: 'Disputed',    icon: '🚨' },
};

const HOURS = Array.from({ length: 24 }, (_, i) => `${i}`);
const DAYS  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

/* ═══════════════════════════════════════════════════════════
   ANIMATED NUMBER COUNTER
═══════════════════════════════════════════════════════════ */
function AnimNum({ to, prefix = '', suffix = '', decimals = 0, duration = 900 }) {
  const [val, setVal] = useState(0);
  const frame = useRef();
  useEffect(() => {
    const start = performance.now();
    const tick  = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 4);
      setVal(to * ease);
      if (p < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [to, duration]);
  const display = decimals ? val.toFixed(decimals) : Math.floor(val).toLocaleString();
  return <>{prefix}{display}{suffix}</>;
}

/* ═══════════════════════════════════════════════════════════
   SPARKLINE SVG
═══════════════════════════════════════════════════════════ */
function Spark({ data, color, height = 40, filled = true }) {
  if (!data?.length || data.length < 2) return null;
  const safeData = data.map(v => (isNaN(v) || v == null) ? 0 : v);
  const max = Math.max(...safeData, 1);
  const w   = 200;
  const pts = safeData.map((v, i) => {
    const x = (i / (safeData.length - 1)) * w;
    const y = height - (v / max) * height * 0.9;
    return `${isNaN(x) ? 0 : x},${isNaN(y) ? height : y}`;
  }).join(' ');
  const area = `0,${height} ${pts} ${w},${height}`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}>
      {filled && (
        <polygon points={area} fill={color} opacity="0.15" />
      )}
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════
   KPI CARD
═══════════════════════════════════════════════════════════ */
function KPICard({ icon, label, value, trend, color, prefix = '', suffix = '', sparkData, decimals = 0 }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #0f0f0f 0%, #161616 100%)',
      border: `1px solid ${color}28`,
      borderRadius: '16px',
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform .2s, box-shadow .2s',
      cursor: 'default',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 32px ${color}20`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Glow blob */}
      <div style={{
        position: 'absolute', top: '-30px', right: '-30px',
        width: '100px', height: '100px',
        background: color, borderRadius: '50%',
        filter: 'blur(40px)', opacity: 0.12, pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{
          width: '40px', height: '40px', borderRadius: '12px',
          background: color + '20', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '20px',
        }}>{icon}</div>
        {trend !== undefined && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '3px',
            fontSize: '12px', fontWeight: 700, padding: '4px 10px',
            borderRadius: '20px',
            background: trend >= 0 ? '#10b98120' : '#ef444420',
            color: trend >= 0 ? '#10b981' : '#ef4444',
          }}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </div>
        )}
      </div>

      <div style={{ fontSize: '28px', fontWeight: 800, color, letterSpacing: '-1px', marginBottom: '4px' }}>
        <AnimNum to={value} prefix={prefix} suffix={suffix} decimals={decimals} />
      </div>
      <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>

      {sparkData && (
        <div style={{ marginTop: '12px', opacity: 0.6 }}>
          <Spark data={sparkData} color={color} height={32} />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SECTION WRAPPER
═══════════════════════════════════════════════════════════ */
function Section({ title, children, action }) {
  return (
    <div style={{ marginBottom: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div className="an-section-title">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   BAR CHART (Mini sparkline bars)
═══════════════════════════════════════════════════════════ */
function BarChart({ data, keyName, color, height = 100, showLabels = false }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => Number(d[keyName]) || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height, position: 'relative' }}>
      {data.map((d, i) => {
        const h = Math.max(((d[keyName] || 0) / max) * height, 2);
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}
            title={`${d.date || i}: ${d[keyName]?.toLocaleString() || 0}`}>
            <div style={{
              width: '100%', height: h,
              background: `linear-gradient(180deg, ${color} 0%, ${color}80 100%)`,
              borderRadius: '3px 3px 0 0',
              transition: 'height .5s cubic-bezier(.34,1.56,.64,1)',
            }} />
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   HEATMAP
═══════════════════════════════════════════════════════════ */
function Heatmap({ matrix = [], days = DAYS }) {
  if (!matrix.length) return null;
  const allVals = matrix.flat();
  const max     = Math.max(...allVals, 1);

  const cellColor = (v) => {
    const ratio = v / max;
    if (ratio === 0)  return '#0d0d0d';
    if (ratio < 0.15) return '#1a0f00';
    if (ratio < 0.30) return '#3d2000';
    if (ratio < 0.50) return '#7c4200';
    if (ratio < 0.70) return '#b36000';
    if (ratio < 0.85) return '#C8860A';
    return '#f5c842';
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `44px repeat(24, 1fr)`,
        gap: '2px',
        minWidth: '580px',
      }}>
        {/* Hour labels */}
        <div />
        {HOURS.map(h => (
          <div key={h} style={{ fontSize: '9px', color: '#444', textAlign: 'center', paddingBottom: '3px' }}>
            {h % 6 === 0 ? `${h}h` : ''}
          </div>
        ))}

        {/* Day rows */}
        {days.map((day, di) => (
          [
            <div key={`label-${di}`} style={{ fontSize: '10px', color: '#555', display: 'flex', alignItems: 'center', fontWeight: 600 }}>
              {day}
            </div>,
            ...(matrix[di] || Array(24).fill(0)).map((v, hi) => (
              <div
                key={`cell-${di}-${hi}`}
                title={`${day} ${hi}:00 — ${v} bookings`}
                style={{
                  height: '20px',
                  borderRadius: '3px',
                  background: cellColor(v),
                  cursor: 'default',
                  transition: 'transform .1s',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.3)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            ))
          ]
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px' }}>
        <span style={{ fontSize: '10px', color: '#444' }}>Low</span>
        {['#0d0d0d','#1a0f00','#3d2000','#7c4200','#b36000','#C8860A','#f5c842'].map(c => (
          <div key={c} style={{ width: '20px', height: '10px', borderRadius: '3px', background: c }} />
        ))}
        <span style={{ fontSize: '10px', color: '#444' }}>High</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DONUT CHART (pure SVG)
═══════════════════════════════════════════════════════════ */
function DonutChart({ segments, size = 140 }) {
  const r        = 48;
  const cx       = size / 2;
  const cy       = size / 2;
  const total    = segments.reduce((s, x) => s + (x.value || 0), 0);
  if (!total) return null;
  let cumAngle   = -Math.PI / 2;

  const arcs = segments.map(seg => {
    const angle   = (seg.value / total) * 2 * Math.PI;
    const x1      = cx + r * Math.cos(cumAngle);
    const y1      = cy + r * Math.sin(cumAngle);
    cumAngle     += angle;
    const x2      = cx + r * Math.cos(cumAngle);
    const y2      = cy + r * Math.sin(cumAngle);
    const large   = angle > Math.PI ? 1 : 0;
    return { ...seg, d: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`, pct: Math.round((seg.value / total) * 100) };
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r + 8} fill="none" stroke="#111" strokeWidth="1" />
      {arcs.map((arc, i) => (
        <path key={i} d={arc.d} fill={arc.color} opacity="0.9">
          <title>{arc.label}: {arc.value} ({arc.pct}%)</title>
        </path>
      ))}
      <circle cx={cx} cy={cy} r={r * 0.6} fill="#0a0a0a" />
      <text x={cx} y={cy - 4} textAnchor="middle" fill="#fff" fontSize="16" fontWeight="800">{total}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="#555" fontSize="9">Total</text>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════
   RING PROGRESS
═══════════════════════════════════════════════════════════ */
function Ring({ pct, color, size = 80, label }) {
  const r   = 34;
  const c   = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
      <svg width={size} height={size} viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#1a1a1a" strokeWidth="8" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={c} strokeDashoffset={off}
          strokeLinecap="round"
          transform="rotate(-90 40 40)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        <text x="40" y="44" textAnchor="middle" fill={color} fontSize="15" fontWeight="800">{pct}%</text>
      </svg>
      {label && <div style={{ fontSize: '11px', color: '#555', textAlign: 'center' }}>{label}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function AdminAnalyticsTab() {
  const [period,  setPeriod]  = useState('month');
  const [loading, setLoading] = useState(true);

  const [overview,    setOverview]    = useState(null);
  const [growth,      setGrowth]      = useState([]);
  const [trend,       setTrend]       = useState([]);
  const [funnel,      setFunnel]      = useState({});
  const [cities,      setCities]      = useState([]);
  const [topPerf,     setTopPerf]     = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [heatmap,     setHeatmap]     = useState({ matrix: [], days: DAYS });
  const [activity,    setActivity]    = useState([]);
  const [retention,   setRetention]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, gr, tr, ct, tp, vt, hm, ac, ret] = await Promise.all([
        adminAPI.getAnalyticsOverview(period),
        adminAPI.getAnalyticsGrowth(period),
        adminAPI.getAnalyticsTrend(period),
        adminAPI.getAnalyticsCities(),
        adminAPI.getAnalyticsTopPerformers(),
        adminAPI.getAnalyticsVendorTypes(),
        adminAPI.getAnalyticsHeatmap(period),
        adminAPI.getAnalyticsActivity(20),
        adminAPI.getAnalyticsRetention(),
      ]);
      setOverview(ov.data);
      setGrowth(gr.data?.growth || []);
      setTrend(tr.data?.trend || []);
      setFunnel(tr.data?.funnel || {});
      setCities(ct.data?.cities || []);
      setTopPerf(tp.data?.topDecorators || []);
      setVendorTypes(vt.data?.vendorTypes || []);
      setHeatmap(hm.data || { matrix: [], days: DAYS });
      setActivity(ac.data?.feed || []);
      setRetention(ret.data);
    } catch (e) {
      console.error('Analytics error', e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  /* ── Derived ── */
  const sparkBookings = trend.map(d => Number(d.bookings) || 0);
  const sparkRevenue  = trend.map(d => Number(d.revenue) || 0);
  const funnelTotal   = Object.values(funnel).reduce((a, b) => a + b, 0);
  const maxCity       = Math.max(...cities.map(c => c.bookings), 1);

  /* ── Loading screen ── */
  if (loading) return (
    <div style={{
      minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '20px',
    }}>
      <div style={{ fontSize: '48px', animation: 'spin 1s linear infinite' }}>📊</div>
      <div style={{ width: '200px', height: '3px', background: '#1a1a1a', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{
          height: '100%', background: 'linear-gradient(90deg, #C8860A, #f5c842)',
          borderRadius: '2px', animation: 'loadbar 1.5s ease-in-out infinite',
        }} />
      </div>
      <p style={{ color: '#444', fontSize: '13px' }}>Analytics load ho raha hai...</p>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes loadbar { 0%{width:0;margin-left:0} 50%{width:60%;margin-left:20%} 100%{width:0;margin-left:100%} }
      `}</style>
    </div>
  );

  return (
    <div className="adm-tab-content" style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif", background: '#0a0a0a', minHeight: '100vh', padding: '0' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        .an-wrap { background: #0a0a0a; padding: 24px; min-height: 100vh; }

        /* ── Period chips ── */
        .an-chip {
          background: #111;
          border: 1px solid #1e1e1e;
          border-radius: 8px;
          padding: 7px 16px;
          font-size: 12px;
          font-weight: 600;
          color: #444;
          cursor: pointer;
          transition: all .15s;
          white-space: nowrap;
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }
        .an-chip:hover { border-color: #333; color: #888; background: #161616; }
        .an-chip.active {
          background: #C8860A;
          border-color: #C8860A;
          color: #000;
          font-weight: 800;
          box-shadow: 0 0 16px #C8860A40;
        }
        .an-chip-refresh {
          background: transparent;
          border: 1px solid #1e1e1e;
          border-radius: 8px;
          padding: 7px 14px;
          font-size: 12px;
          color: #333;
          cursor: pointer;
          transition: all .15s;
        }
        .an-chip-refresh:hover { border-color: #333; color: #666; }

        /* ── Section title ── */
        .an-section-title {
          font-size: 11px;
          font-weight: 700;
          color: #333;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin: 0 0 14px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .an-section-title::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #161616;
        }

        /* ── Cards base ── */
        .an-card {
          background: #111;
          border: 1px solid #1a1a1a;
          border-radius: 14px;
          padding: 20px;
          transition: border-color .2s;
        }
        .an-card:hover { border-color: #252525; }

        /* ── Vendor chips ── */
        .an-vendor-chip {
          background: #111;
          border: 1px solid #1a1a1a;
          border-radius: 12px;
          padding: 16px 14px;
          text-align: center;
          transition: all .2s;
          cursor: default;
        }
        .an-vendor-chip:hover {
          border-color: #C8860A30;
          background: #141414;
          transform: translateY(-2px);
          box-shadow: 0 4px 20px #00000060;
        }

        /* ── Performer rows ── */
        .an-perf-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 16px;
          background: #111;
          border: 1px solid #1a1a1a;
          border-radius: 12px;
          margin-bottom: 6px;
          transition: all .15s;
        }
        .an-perf-row:hover {
          border-color: #C8860A25;
          background: #141414;
        }

        /* ── City rows ── */
        .an-city-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px 0;
          border-bottom: 1px solid #141414;
        }
        .an-city-row:last-child { border-bottom: none; }

        /* ── Activity feed ── */
        .an-activity-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 11px 14px;
          background: #111;
          border: 1px solid #1a1a1a;
          border-radius: 10px;
          margin-bottom: 5px;
          transition: all .15s;
        }
        .an-activity-item:hover { border-color: #252525; background: #141414; }

        /* ── Funnel cards ── */
        .an-funnel-card {
          flex: 1;
          min-width: 110px;
          background: #111;
          border-radius: 12px;
          padding: 14px 16px;
          border: 1px solid #1a1a1a;
          transition: all .2s;
          cursor: default;
        }
        .an-funnel-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px #00000050;
        }

        /* ── Divider ── */
        .an-divider { height: 1px; background: #141414; margin: 24px 0; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .an-fade { animation: fadeInUp .35s ease both; }

        @keyframes ping { 0%,100%{opacity:1} 50%{opacity:.2} }
      `}</style>
    <div className="an-wrap">


      {/* ── Period Selector ── */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '32px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '6px', background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '10px', padding: '4px' }}>
          {PERIODS.map(p => (
            <button key={p.key} className={`an-chip ${period === p.key ? 'active' : ''}`}
              onClick={() => setPeriod(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
        <button className="an-chip-refresh" onClick={load} style={{ marginLeft: 'auto' }}>↺ Refresh</button>
      </div>

      {/* ══════════════════════════════════════════════
          1. KPI GRID
      ══════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '10px', marginBottom: '32px' }}>
        <KPICard icon="👥" label="Naye Users"      value={overview?.newUsers || 0}          trend={overview?.newUsersTrend}    color="#3b82f6" sparkData={sparkBookings} />
        <KPICard icon="🎨" label="Naye Vendors"    value={overview?.newDecorators || 0}     color="#C8860A" sparkData={sparkBookings} />
        <KPICard icon="📋" label="Naye Bookings"   value={overview?.newBookings || 0}       trend={overview?.newBookingsTrend} color="#8b5cf6" sparkData={sparkBookings} />
        <KPICard icon="✅" label="Completed"       value={overview?.completedBookings || 0} color="#10b981" sparkData={sparkBookings} />
        <KPICard icon="💰" label="Revenue"         value={overview?.revenue || 0}           trend={overview?.revenueTrend}    color="#f0c55a" prefix="₹" sparkData={sparkRevenue} />
        <KPICard icon="🎯" label="Conversion Rate" value={overview?.conversionRate || 0}    color="#ec4899" suffix="%" />
      </div>

      {/* ══════════════════════════════════════════════
          2. BOOKING FUNNEL + DONUT
      ══════════════════════════════════════════════ */}
      <Section title="🎯 Booking Funnel">
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Donut */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            <DonutChart
              segments={Object.entries(funnel).map(([k, v]) => ({ label: STATUS_META[k]?.label || k, value: v, color: STATUS_META[k]?.color || '#888' }))}
              size={160}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {Object.entries(funnel).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: STATUS_META[k]?.color, flexShrink: 0 }} />
                  <span style={{ color: '#666' }}>{STATUS_META[k]?.label}</span>
                  <span style={{ color: '#aaa', fontWeight: 600, marginLeft: 'auto' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Funnel bars */}
          <div style={{ flex: 1, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {Object.entries(funnel).map(([status, count]) => {
              const meta = STATUS_META[status] || {};
              const pct  = funnelTotal > 0 ? Math.round((count / funnelTotal) * 100) : 0;
              return (
                <div key={status} className="an-funnel-card" style={{ borderColor: meta.color + '22' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = meta.color + '66'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = meta.color + '22'}>
                  <div style={{ fontSize: '10px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                    {meta.icon} {meta.label}
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 800, color: meta.color, lineHeight: 1 }}>{count}</div>
                  <div style={{ height: '3px', background: '#1a1a1a', borderRadius: '2px', margin: '10px 0 4px' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: meta.color, borderRadius: '2px', transition: 'width .8s ease' }} />
                  </div>
                  <div style={{ fontSize: '10px', color: '#444' }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════
          3. TREND CHARTS
      ══════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '32px' }}>
        {/* Bookings */}
        <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: '12px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', color: '#ccc', fontWeight: 700 }}>📈 Bookings Trend</h4>
            <span style={{ fontSize: '13px', color: '#8b5cf6', fontWeight: 700 }}>
              {trend.reduce((a, d) => a + d.bookings, 0)} total
            </span>
          </div>
          <Spark data={trend.map(d => d.bookings)} color="#8b5cf6" height={80} />
          <div style={{ marginTop: '10px' }}>
            <BarChart data={trend} keyName="bookings" color="#8b5cf6" height={60} />
          </div>
        </div>

        {/* Revenue */}
        <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: '12px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', color: '#ccc', fontWeight: 700 }}>💰 Revenue Trend</h4>
            <span style={{ fontSize: '13px', color: '#C8860A', fontWeight: 700 }}>
              ₹{(trend.reduce((a, d) => a + d.revenue, 0) / 1000).toFixed(1)}k
            </span>
          </div>
          <Spark data={trend.map(d => d.revenue)} color="#C8860A" height={80} />
          <div style={{ marginTop: '10px' }}>
            <BarChart data={trend} keyName="revenue" color="#C8860A" height={60} />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          4. USER GROWTH (Stacked)
      ══════════════════════════════════════════════ */}
      <Section title="👥 User Growth — Clients vs Vendors">
        <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: '12px', padding: '20px' }}>
          <div style={{ display: 'flex', gap: '3px', alignItems: 'flex-end', height: '100px' }}>
            {growth.map((d, i) => {
              const maxG  = Math.max(...growth.map(g => (g.client || 0) + (g.decorator || 0)), 1);
              const total = (d.client || 0) + (d.decorator || 0);
              const h     = (total / maxG) * 100;
              const dPct  = total > 0 ? ((d.decorator || 0) / total * 100) : 0;
              return (
                <div key={i} title={`${d.date}: ${d.client} clients, ${d.decorator} vendors`}
                  style={{ flex: 1, height: `${h}px`, display: 'flex', flexDirection: 'column', borderRadius: '3px 3px 0 0', overflow: 'hidden', minHeight: '2px' }}>
                  <div style={{ height: `${dPct}%`, background: '#C8860A', minHeight: dPct > 0 ? '2px' : '0' }} />
                  <div style={{ flex: 1, background: '#3b82f6' }} />
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#3b82f6' }}>
              <div style={{ width: '10px', height: '10px', background: '#3b82f6', borderRadius: '2px' }} /> Clients
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#C8860A' }}>
              <div style={{ width: '10px', height: '10px', background: '#C8860A', borderRadius: '2px' }} /> Vendors
            </div>
            <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#444' }}>
              Total: {growth.reduce((a, d) => a + (d.client || 0) + (d.decorator || 0), 0)}
            </span>
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════
          5. CITY BREAKDOWN
      ══════════════════════════════════════════════ */}
      <Section title="📍 City-wise Performance">
        <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: '12px', padding: '20px' }}>
          {cities.map((c, i) => (
            <div key={c.city} className="an-city-row">
              <div style={{ width: '100px', fontSize: '13px', fontWeight: 600, color: '#ccc' }}>
                <span style={{ fontSize: '10px', color: '#444', marginRight: '6px' }}>#{i + 1}</span>
                {c.city || 'N/A'}
              </div>
              <div style={{ flex: 1, height: '6px', background: '#141414', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${(c.bookings / maxCity) * 100}%`, height: '100%',
                  background: `linear-gradient(90deg, #C8860A, #f5c842)`,
                  borderRadius: '3px', transition: 'width .8s ease',
                }} />
              </div>
              <div style={{ display: 'flex', gap: '14px', minWidth: '160px', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '11px', color: '#3b82f6' }}>👥 {c.users}</span>
                <span style={{ fontSize: '11px', color: '#C8860A' }}>🎨 {c.decorators}</span>
                <span style={{ fontSize: '11px', color: '#8b5cf6' }}>📋 {c.bookings}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ══════════════════════════════════════════════
          6. VENDOR TYPES
      ══════════════════════════════════════════════ */}
      <Section title="🎪 Vendor Type Breakdown">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
          {vendorTypes.map(v => {
            const verifiedPct = v.total > 0 ? Math.round((v.verified / v.total) * 100) : 0;
            return (
              <div key={v._id} className="an-vendor-chip">
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>{VENDOR_ICONS[v._id] || '🎯'}</div>
                <div style={{ fontSize: '11px', color: '#555', textTransform: 'capitalize', marginBottom: '6px', letterSpacing: '0.3px' }}>
                  {v._id?.replace(/_/g, ' ')}
                </div>
                <div style={{ fontSize: '24px', fontWeight: 800, color: '#C8860A', lineHeight: 1 }}>{v.total}</div>
                <div style={{ height: '3px', background: '#1a1a1a', borderRadius: '2px', margin: '8px 0 6px' }}>
                  <div style={{ width: `${verifiedPct}%`, height: '100%', background: '#10b981', borderRadius: '2px', transition: 'width .8s' }} />
                </div>
                <div style={{ fontSize: '10px', color: '#444' }}>
                  ✅ {v.verified} &nbsp;·&nbsp; ⭐ {(v.avgRating || 0).toFixed(1)}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ══════════════════════════════════════════════
          7. TOP PERFORMERS
      ══════════════════════════════════════════════ */}
      <Section title="🏆 Top Performing Vendors">
        {topPerf.slice(0, 8).map((d, i) => (
          <div key={d._id} className="an-perf-row">
            {/* Rank */}
            <div style={{
              width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
              background: i === 0 ? '#fbbf2420' : i === 1 ? '#9ca3af20' : i === 2 ? '#b45309 20' : '#111',
              color: i === 0 ? '#fbbf24' : i === 1 ? '#9ca3af' : i === 2 ? '#b45309' : '#333',
              border: `1px solid ${i < 3 ? 'currentColor' : '#1a1a1a'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '13px', fontWeight: 800,
            }}>{i + 1}</div>

            {/* Info */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#e0e0e0', marginBottom: '2px' }}>
                {d.businessName}
              </div>
              <div style={{ fontSize: '11px', color: '#555' }}>
                📍 {d.city} &nbsp;·&nbsp; {VENDOR_ICONS[d.vendorType] || '🎯'} {d.vendorType?.replace(/_/g, ' ')}
              </div>
            </div>

            {/* Stats */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '16px', color: '#C8860A', fontWeight: 800 }}>
                ₹{((d.totalEarnings || 0) / 1000).toFixed(1)}k
              </div>
              <div style={{ fontSize: '11px', color: '#444' }}>
                ⭐ {d.rating} &nbsp;·&nbsp; 📋 {d.totalBookings}
              </div>
            </div>

            {/* Mini bar showing relative performance */}
            <div style={{ width: '60px', flexShrink: 0 }}>
              <div style={{ height: '4px', background: '#1a1a1a', borderRadius: '2px' }}>
                <div style={{
                  width: `${((d.totalEarnings || 0) / (topPerf[0]?.totalEarnings || 1)) * 100}%`,
                  height: '100%', background: i === 0 ? '#fbbf24' : '#C8860A',
                  borderRadius: '2px', transition: 'width .8s',
                }} />
              </div>
            </div>
          </div>
        ))}
      </Section>

      {/* ══════════════════════════════════════════════
          8. HEATMAP
      ══════════════════════════════════════════════ */}
      <Section title="🔥 Booking Activity Heatmap (Day × Hour)">
        <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: '12px', padding: '20px' }}>
          <Heatmap matrix={heatmap.matrix} days={heatmap.days} />
          <div style={{ marginTop: '12px', fontSize: '11px', color: '#333' }}>
            💡 Har cell = ek ghante mein bookings ka count. Hover karo details ke liye.
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════
          9. RETENTION
      ══════════════════════════════════════════════ */}
      {retention && (
        <Section title="🔁 User Retention">
          <div style={{ background: '#111', border: '1px solid #1a1a1a', borderRadius: '12px', padding: '20px' }}>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '24px' }}>
              <Ring pct={retention.retentionRate} color="#C8860A" size={90} label="Retention" />
              <Ring pct={Math.round((retention.activeClients / retention.totalClients) * 100)} color="#10b981" size={90} label="Active Rate" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { label: 'Total Clients',  value: retention.totalClients,  color: '#3b82f6' },
                  { label: 'Active Clients', value: retention.activeClients, color: '#10b981' },
                ].map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: '#666', width: '100px' }}>{s.label}</span>
                    <span style={{ fontSize: '16px', fontWeight: 800, color: s.color }}>
                      {s.value?.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Repeat booking distribution */}
            <div style={{ fontSize: '12px', color: '#444', marginBottom: '10px' }}>Repeat Booking Distribution</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {(retention.repeatClients || []).map(r => {
                const maxUsers = Math.max(...retention.repeatClients.map(x => x.users), 1);
                const pct = Math.round((r.users / maxUsers) * 100);
                return (
                  <div key={r._id} style={{
                    flex: '1', minWidth: '80px', background: '#111',
                    border: '1px solid #1a1a1a', borderRadius: '12px', padding: '14px',
                    textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '22px', fontWeight: 800, color: '#C8860A' }}>{r.users}</div>
                    <div style={{ fontSize: '10px', color: '#444', margin: '6px 0 8px' }}>
                      {r._id === 1 ? '1 booking' : `${r._id} bookings`}
                    </div>
                    <div style={{ height: '3px', background: '#1a1a1a', borderRadius: '2px' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#C8860A', borderRadius: '2px' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      )}

      {/* ══════════════════════════════════════════════
          10. ACTIVITY FEED
      ══════════════════════════════════════════════ */}
      <Section title="⚡ Live Activity Feed"
        action={
          <span style={{ fontSize: '11px', color: '#2d2d2d', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'ping 2s infinite' }} />
            Live
            <style>{`@keyframes ping { 0%,100%{opacity:1}50%{opacity:.3} }`}</style>
          </span>
        }
      >
        {activity.map((item, i) => (
          <div key={i} className="an-activity-item">
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px', background: '#111',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', flexShrink: 0,
            }}>{item.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', color: '#ccc', lineHeight: 1.4 }}>{item.message}</div>
              {item.amount && (
                <span style={{ fontSize: '11px', color: '#C8860A', fontWeight: 700 }}>
                  ₹{item.amount?.toLocaleString()}
                </span>
              )}
            </div>
            <div style={{ fontSize: '10px', color: '#2d2d2d', flexShrink: 0 }}>
              {new Date(item.time).toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}
        {activity.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#333' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚡</div>
            <div>Koi activity nahi</div>
          </div>
        )}
      </Section>

    </div>
    </div>
  );
}